import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { PaperclipIcon } from '../../components/icons/Icons';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export default function ExpenseDetailModal({ report, onClose, appId }) {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [approverName, setApproverName] = useState('');
    const [paidByName, setPaidByName] = useState('');
    
    // --- NEW: State to track zip generation status ---
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (!report) return;
        
        const itemsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/expenseReports/${report.id}/expenseItems`),
            orderBy('expenseDate', 'asc')
        );

        const unsubscribe = onSnapshot(itemsQuery, (snapshot) => {
            const fetchedItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setItems(fetchedItems);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [report, appId]);

    useEffect(() => {
        const fetchApproverName = async () => {
            if (report?.managerId) {
                const userDocRef = doc(db, `/artifacts/${appId}/users/${report.managerId}`);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    setApproverName(userDocSnap.data().name);
                } else {
                    setApproverName('Approver not found');
                }
            } else {
                setApproverName('N/A');
            }
        };

        fetchApproverName();
    }, [report, appId]);

    useEffect(() => {
        if (report?.auditTrail && Array.isArray(report.auditTrail)) {
            const paidEntry = report.auditTrail.find(entry => entry.action === 'Reimbursed');
            if (paidEntry) {
                setPaidByName(paidEntry.user);
            }
        }
    }, [report]);

    // --- NEW: Function to bundle receipts into a ZIP file ---
    const downloadAllReceipts = async () => {
        const itemsWithReceipts = items.filter(item => item.receiptFile && item.receiptFile.url);
        
        if (itemsWithReceipts.length === 0) {
            alert("No receipts found to download.");
            return;
        }

        setIsDownloading(true);
        const zip = new JSZip();
        const folderName = `Expense_Report_${report.reportNumber}_Receipts`;
        const folder = zip.folder(folderName);

        try {
            // Create an array of promises to fetch all files in parallel
            const promiseArray = itemsWithReceipts.map(async (item) => {
                try {
                    // Fetch the file as a blob
                    const response = await fetch(item.receiptFile.url);
                    if (!response.ok) throw new Error(`Failed to fetch ${item.receiptFile.name}`);
                    const blob = await response.blob();
                    
                    // Determine filename (fallback to ID if name is missing)
                    const fileName = item.receiptFile.name || `receipt_${item.id}.jpg`;
                    
                    // Add to zip folder
                    folder.file(fileName, blob);
                } catch (err) {
                    console.error(`Error downloading ${item.receiptFile.name}:`, err);
                    // We continue even if one fails
                }
            });

            // Wait for all fetches to complete
            await Promise.all(promiseArray);

            // Generate the zip file
            const content = await zip.generateAsync({ type: "blob" });
            
            // Trigger the download
            saveAs(content, `${folderName}.zip`);

        } catch (error) {
            console.error("Error creating zip file:", error);
            alert("An error occurred while generating the zip file.");
        } finally {
            setIsDownloading(false);
        }
    };

    const DetailItem = ({ label, value }) => (
        <div>
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 text-sm text-gray-900 font-semibold">{value}</dd>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-[#213368]">Expense Report Details</h2>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{report.reportNumber}</span>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mb-6 pb-4 border-b">
                    <DetailItem label="Report Name" value={report.reportName} />
                    <DetailItem label="Submitted By" value={report.requesterName} />
                    <DetailItem label="Approver" value={approverName || 'Loading...'} />
                    <DetailItem label="Date Submitted" value={report.createdAt?.toDate().toLocaleDateString() || 'N/A'} />
                    <DetailItem label="Entity" value={report.entity} />
                    <DetailItem label="Total Reimbursable" value={`${report.reimbursementCurrency} ${report.totalAmount.toFixed(2)}`} />
                    <DetailItem label="Status" value={report.status} />
                    
                    {report.reimbursementDate && (
                        <DetailItem label="Payment Date" value={report.reimbursementDate.toDate().toLocaleDateString()} />
                    )}
                    {paidByName && (
                         <DetailItem label="Paid By" value={paidByName} />
                    )}

                    {report.approvalComments && <div className="col-span-full mt-2"><DetailItem label="Manager's Comments" value={report.approvalComments} /></div>}
                </div>

                <div className="flex-grow overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Itemized Expenses</h3>
                        {/* --- UPDATED: Button with Loading State --- */}
                        {items.some(item => item.receiptFile?.url) && (
                            <button 
                                onClick={downloadAllReceipts}
                                disabled={isDownloading}
                                className={`text-sm px-3 py-1 rounded border transition-colors flex items-center gap-2
                                    ${isDownloading 
                                        ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-wait' 
                                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'
                                    }`}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                        Creating Zip...
                                    </>
                                ) : (
                                    'Download All Receipts (Zip)'
                                )}
                            </button>
                        )}
                    </div>

                    {isLoading ? <LoadingSpinner /> : (
                        <div className="border rounded-lg overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor & Category</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Original Cost</th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reimbursable</th>
                                        <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm">{item.expenseDate.toDate().toLocaleDateString()}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                <div className="font-medium">{item.vendor}</div>
                                                <div className="text-gray-500">{item.category}</div>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 min-w-[150px]">{item.description}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                                                {`${item.currency} ${item.amount.toFixed(2)}`}
                                                {item.effectiveConversionRate && <div className="text-xs text-gray-400">@{item.effectiveConversionRate.toFixed(4)}</div>}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-medium">{`${report.reimbursementCurrency} ${item.reimbursableAmount.toFixed(2)}`}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-center">
                                                {item.receiptFile && item.receiptFile.url ? (
                                                    <a href={item.receiptFile.url} target="_blank" rel="noopener noreferrer" className="inline-block text-blue-600 hover:text-blue-800" title={item.receiptFile.name}>
                                                        <PaperclipIcon />
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-gray-400">No Receipt</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                     {items.length === 0 && !isLoading && (
                        <p className="text-center text-gray-500 py-8">No expense items found for this report.</p>
                    )}
                </div>

                <div className="flex justify-end pt-6 mt-4 border-t">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Close</button>
                </div>
            </div>
        </div>
    );
}